import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { FMT } from "./shared";

// ─── TOAST SYSTEM ───
// Usage: wrap app in <ToastProvider>, then const toast = useToast();
//   toast.success("Pedido completado");
//   toast.error("Error al guardar");
//   toast.info("Factura enviada");
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }, []);
  const toast = {
    success: (msg) => addToast(msg, "success"),
    error: (msg) => addToast(msg, "error"),
    info: (msg) => addToast(msg, "info"),
  };
  return <ToastContext.Provider value={toast}>
    {children}
    {toasts.length > 0 && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 99999, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", pointerEvents: "none" }}>
      {toasts.map(t => {
        const colors = { success: { bg: "#16A34A", icon: "✅" }, error: { bg: "#DC2626", icon: "❌" }, info: { bg: "#2563EB", icon: "ℹ️" } };
        const c = colors[t.type] || colors.info;
        return <div key={t.id} style={{ background: c.bg, color: "white", padding: "12px 24px", borderRadius: 14, fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 10, boxShadow: "0 8px 30px rgba(0,0,0,.3)", animation: "fadeIn .3s", fontFamily: "'DM Sans',sans-serif", pointerEvents: "auto", maxWidth: 400 }}>
          <span>{c.icon}</span>{t.msg}
        </div>;
      })}
    </div>}
  </ToastContext.Provider>;
}
export function useToast() { return useContext(ToastContext); }

// ─── CONFIRM MODAL ───
// Usage: <ConfirmModal open={show} title="¿Seguro?" message="..." onConfirm={fn} onCancel={fn} confirmText="Sí, eliminar" danger />
export function ConfirmModal({ open, title, message, onConfirm, onCancel, confirmText = "Confirmar", danger = false }) {
  if (!open) return null;
  return <div style={{ position: "fixed", inset: 0, zIndex: 99998, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onCancel}>
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)" }} />
    <div onClick={e => e.stopPropagation()} style={{ background: "#1E293B", borderRadius: 20, padding: "28px 24px", maxWidth: 400, width: "100%", position: "relative", boxShadow: "0 20px 60px rgba(0,0,0,.5)", animation: "fadeIn .2s", border: "1px solid rgba(255,255,255,.1)" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>{danger ? "⚠️" : "❓"}</div>
        <div style={{ fontFamily: "'Baloo 2',cursive", fontWeight: 800, fontSize: 20, color: "white", marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: "#94A3B8", lineHeight: 1.5 }}>{message}</div>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: 14, borderRadius: 12, border: "1px solid rgba(255,255,255,.1)", background: "transparent", color: "#94A3B8", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>Cancelar</button>
        <button onClick={onConfirm} style={{ flex: 1, padding: 14, borderRadius: 12, border: "none", background: danger ? "linear-gradient(135deg,#DC2626,#B91C1C)" : "linear-gradient(135deg,#16A34A,#15803D)", color: "white", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>{confirmText}</button>
      </div>
    </div>
  </div>;
}

export function Floaters() {
  const e = ["🍕","🍪","🧃","🍎","🥪","🍦","🍫","🍇","🍗","🍌"];
  return <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,overflow:"hidden"}}>{e.map((x,i)=><div key={i} style={{position:"absolute",fontSize:`${20+Math.random()*20}px`,left:`${5+i*10}%`,top:`${10+(i%3)*30}%`,opacity:.07,animation:`floatB ${8+i*2}s ease-in-out infinite alternate`,animationDelay:`${i*.5}s`}}>{x}</div>)}</div>;
}

export function Confetti({active}){
  if(!active)return null;
  const c=["#FF6B6B","#4ECDC4","#FFE66D","#A78BFA","#FB923C","#34D399"];
  return <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:9999}}>{Array.from({length:50}).map((_,i)=><div key={i} style={{position:"absolute",width:`${8+Math.random()*8}px`,height:`${8+Math.random()*8}px`,borderRadius:Math.random()>.5?"50%":"2px",background:c[i%c.length],left:`${Math.random()*100}%`,top:"-10px",animation:`confetti ${1.5+Math.random()*2}s ease-out forwards`,animationDelay:`${Math.random()*.5}s`,transform:`rotate(${Math.random()*360}deg)`}}/>)}</div>;
}

export function Spinner({text}) {
  return <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh",gap:20,fontFamily:"'Fredoka',sans-serif"}}>
    <div style={{width:48,height:48,border:"4px solid #E2E8F0",borderTopColor:"#FB923C",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
    <div style={{fontSize:18,color:"#64748B",fontWeight:500}}>{text||"Cargando..."}</div>
  </div>;
}

export function ProductVisual({product, size = 48}) {
  if (product.image) return <img src={product.image} alt={product.name} style={{width:size,height:size,borderRadius:size>40?12:8,objectFit:"cover"}} />;
  return <span style={{fontSize:size,lineHeight:1}}>{product.emoji}</span>;
}

export function PCard({product:p,quantity:q,onAdd,onRemove}){
  const h=q>0;
  return <div onClick={onAdd} style={{background:h?"rgba(251,146,60,.15)":"#1E293B",borderRadius:20,padding:"20px 16px",display:"flex",flexDirection:"column",alignItems:"center",gap:8,border:h?"3px solid #FB923C":"1px solid rgba(255,255,255,.06)",transition:"all .3s cubic-bezier(.4,0,.2,1)",cursor:"pointer",position:"relative",boxShadow:h?"0 8px 25px rgba(251,146,60,.25)":"none",transform:h?"scale(1.03)":"scale(1)"}}>
    {h&&<div style={{position:"absolute",top:-10,right:-10,background:"linear-gradient(135deg,#FB923C,#F97316)",color:"white",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Fredoka'",fontWeight:700,fontSize:16,boxShadow:"0 3px 10px rgba(249,115,22,.4)",animation:"popIn .3s cubic-bezier(.68,-.55,.27,1.55)"}}>{q}</div>}
    <div><ProductVisual product={p} size={48}/></div>
    <div style={{fontFamily:"'Fredoka'",fontWeight:600,fontSize:14,color:"white",textAlign:"center",lineHeight:1.2}}>{p.name}</div>
    <div style={{fontFamily:"'Baloo 2',cursive",fontWeight:800,fontSize:18,color:"#4ADE80"}}>{FMT(p.price)}</div>
    {h&&<button onClick={e=>{e.stopPropagation();onRemove()}} style={{background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.3)",borderRadius:12,padding:"4px 14px",fontFamily:"'Fredoka'",fontWeight:600,fontSize:13,color:"#EF4444",cursor:"pointer"}}>Quitar ✕</button>}
  </div>;
}

// ─── PAGINATION ───
export function usePagination(items, perPage = 20) {
  const [page, setPage] = useState(0);
  // Reset page if items change significantly
  useEffect(() => { setPage(0); }, [items.length]);
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const saferPage = Math.min(page, totalPages - 1);
  const paged = items.slice(saferPage * perPage, (saferPage + 1) * perPage);
  return { paged, page: saferPage, totalPages, setPage, total: items.length };
}

export function Pagination({ page, totalPages, setPage, total }) {
  if (totalPages <= 1) return null;
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "16px 0" }}>
    <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} style={{ padding: "8px 14px", borderRadius: 10, border: "none", background: page === 0 ? "#0F172A" : "#1E293B", color: page === 0 ? "#475569" : "white", fontWeight: 700, fontSize: 14, cursor: page === 0 ? "not-allowed" : "pointer" }}>‹</button>
    <span style={{ fontSize: 13, color: "#94A3B8", fontWeight: 600 }}>{page + 1} / {totalPages} <span style={{ color: "#475569" }}>({total})</span></span>
    <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} style={{ padding: "8px 14px", borderRadius: 10, border: "none", background: page >= totalPages - 1 ? "#0F172A" : "#1E293B", color: page >= totalPages - 1 ? "#475569" : "white", fontWeight: 700, fontSize: 14, cursor: page >= totalPages - 1 ? "not-allowed" : "pointer" }}>›</button>
  </div>;
}

// ─── UPGRADE NUDGE ───
// Small inline badge shown on locked features.
// Usage: <UpgradeNudge planName="Basic" message="..." />
export function UpgradeNudge({ planName, message, style = {} }) {
  return (
    <div title={message || `Disponible en plan ${planName}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, ...style }}>
      <span style={{ fontSize: 12, cursor: "help" }}>🔒</span>
      {planName && <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", background: "rgba(148,163,184,.1)", padding: "1px 6px", borderRadius: 5 }}>{planName}+</span>}
    </div>
  );
}

// ─── UPGRADE MODAL ───
// Full modal dialog shown when a user tries to use a locked feature.
export function UpgradeModal({ feature, message, planName, onClose }) {
  if (!feature && !message) return null;
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 2000 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#1E293B", borderRadius: 24, padding: "32px 28px", width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,.5)", border: "1px solid rgba(255,255,255,.05)", fontFamily: "'DM Sans'" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>⭐</div>
          <h2 style={{ fontFamily: "'Baloo 2',cursive", fontWeight: 800, fontSize: 22, color: "white", marginBottom: 8 }}>Función Premium</h2>
          <p style={{ fontSize: 14, color: "#94A3B8", lineHeight: 1.6 }}>{message || `Esta función está disponible en el plan ${planName || "Basic"} o superior.`}</p>
        </div>
        <div style={{ background: "rgba(59,130,246,.08)", border: "1px solid rgba(59,130,246,.2)", borderRadius: 12, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: "#93C5FD", lineHeight: 1.5 }}>
          💡 Contactá al administrador de La Tiendita CR para actualizar tu plan y desbloquear esta función.
        </div>
        <button onClick={onClose} style={{ width: "100%", padding: 14, borderRadius: 14, border: "none", background: "linear-gradient(135deg,#3B82F6,#2563EB)", color: "white", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "'Baloo 2',cursive" }}>Entendido</button>
      </div>
    </div>
  );
}
